import React from "react";
import { Dropdown } from "semantic-ui-react";
import Avatar from "./Avatar";
import Body from "./Body";
import LikeButton from "./LikeButton";
import ReplyComposer from "./ReplyComposer";
import { ReplyData } from "./types";

interface ReplyProps {
  reply: ReplyData;
  replyingToId: string | null;
  currentUserName: string;
  currentUserProfilePictureUrl?: string;
  composerId?: string;
  isNested?: boolean;
  onReply: (reply: ReplyData) => void;
  onCancelReply: () => void;
  onReplySubmit: (commentId: number, value: string) => Promise<void>;
  onDeleteReply?: (commentId: number, replyId?: number) => void;
}

const Reply = ({
  reply,
  replyingToId,
  currentUserName,
  currentUserProfilePictureUrl,
  composerId,
  isNested = false,
  onReply,
  onCancelReply,
  onReplySubmit,
  onDeleteReply,
}: ReplyProps) => {
  const composerTarget =
    replyingToId === reply.id
      ? reply
      : reply.replies?.find((nestedReply) => nestedReply.id === replyingToId);
  const hasNestedList = Boolean(reply.replies?.length || composerTarget);

  return (
    <li className="reply" id={`community-reply-${reply.id}`}>
      <Avatar
        className="reply-avatar"
        name={reply.author}
        profilePictureUrl={reply.profilePictureUrl}
      />
      <div className="reply-body">
        <div className="reply-head">
          <span className="reply-author">{reply.author}</span>
          {reply.isAuthor && <span className="op-pill">Author</span>}
          <span className="reply-time">{reply.time}</span>
          {reply.canDelete && (
            <Dropdown icon="ellipsis horizontal" pointing="top right">
              <Dropdown.Menu>
                <Dropdown.Item
                  text="Delete"
                  onClick={() =>
                    onDeleteReply?.(
                      reply.commentId,
                      isNested ? reply.recordId : undefined,
                    )
                  }
                />
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>
        <Body className="reply-text" content={reply.text} />
        <div className="reply-mini-actions">
          <LikeButton
            initialCount={reply.likes}
            initiallyLiked={reply.liked}
            label="Like this reply"
            likedLabel="You liked this reply"
          />
          <button
            className="reply-mini-btn"
            type="button"
            aria-expanded={replyingToId === reply.id || undefined}
            onClick={() => onReply(reply)}
          >
            ↩ Reply
          </button>
        </div>

        {!isNested && hasNestedList && (
          <ol className="reply-list reply-list--nested">
            {reply.replies?.map((nestedReply) => (
              <Reply
                reply={nestedReply}
                replyingToId={replyingToId}
                currentUserName={currentUserName}
                currentUserProfilePictureUrl={currentUserProfilePictureUrl}
                isNested
                onReply={onReply}
                onCancelReply={onCancelReply}
                onReplySubmit={onReplySubmit}
                onDeleteReply={onDeleteReply}
                key={nestedReply.id}
              />
            ))}
            {composerTarget && (
              <ReplyComposer
                id={composerId}
                currentUserName={currentUserName}
                currentUserProfilePictureUrl={currentUserProfilePictureUrl}
                parentAuthor={composerTarget.author}
                onCancel={onCancelReply}
                onSubmit={(value) =>
                  onReplySubmit(composerTarget.commentId, value)
                }
              />
            )}
          </ol>
        )}
      </div>
    </li>
  );
};

export default Reply;
